// 2026-06-20 (user request rekonsiliasi): pisahkan tampilan "Penerimaan Invoice"
// (universe sama dgn Ringkasan Keuangan) vs "Pemasukan Lainnya" (petty cash
// return + invoice TEST/internal/retensi) — total = mutasi masuk.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";

export default function BankHistoryTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bank-history");
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e.response?.data?.detail || "Gagal memuat history bank");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-xs italic text-slate-400" data-testid="bank-history-loading">Memuat history bank…</div>;
  if (error) return <div className="bg-white border border-slate-200 p-6 text-center text-[11px] text-slate-500" data-testid="bank-history-error">{error}</div>;

  // Waktu pembaruan terakhir = tanggal mutasi terbaru di seluruh rekening.
  const lastUpdate = rows.reduce((mx, r) => (r.last_mutation_date && r.last_mutation_date > mx ? r.last_mutation_date : mx), "");

  return (
    <div className="bg-white border border-slate-200" data-testid="bank-history-tab">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <Landmark size={15} className="text-[#0B1D3A]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Riwayat Bank — Rekening Perusahaan</div>
        <span className="text-[10px] text-slate-400 italic">Mutasi masuk = pembayaran invoice · Mutasi keluar = pengeluaran proyek via rekening ini</span>
        {/* 2026-06-20 (user request): disclaimer — BUKAN saldo aktual rekening
            bank — + waktu pembaruan terakhir. */}
        <span className="w-full text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1" data-testid="bh-disclaimer">
          Nilai ini dihitung dari transaksi masuk dan keluar yang dicatat di sistem, bukan saldo aktual rekening bank.
          {" · "}Pembaruan terakhir: <b className="tabular-nums" data-testid="bh-last-update">{lastUpdate ? fmtDate(lastUpdate) : "—"}</b>
        </span>
      </div>
      {/* 2026-06-20 (user request): ringkasan agregat SEMUA bank — jumlah mutasi + saldo gabungan. */}
      {rows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50/60" data-testid="bank-history-totals">
          {[
            { label: "Jumlah Mutasi (Semua Bank)", value: String(rows.reduce((a, r) => a + (r.mutasi_count || 0), 0)), cls: "text-[#0B1D3A]", testid: "bh-total-mutasi" },
            { label: "Penerimaan Invoice (=Ringkasan)", value: formatIDR(rows.reduce((a, r) => a + Number(r.total_masuk_invoice || 0), 0)), cls: "text-emerald-700", testid: "bh-invoice-total" },
            { label: "Pemasukan Lainnya", value: formatIDR(rows.reduce((a, r) => a + Number(r.total_masuk_other || 0), 0)), cls: "text-amber-700", testid: "bh-other-total" },
            { label: "Total Mutasi Masuk", value: formatIDR(rows.reduce((a, r) => a + Number(r.total_masuk || 0), 0)), cls: "text-[#0B1D3A]", testid: "bh-total-masuk" },
            { label: "Total Mutasi Keluar", value: formatIDR(rows.reduce((a, r) => a + Number(r.total_keluar || 0), 0)), cls: "text-rose-700", testid: "bh-total-keluar" },
            { label: "Saldo Berdasarkan Mutasi Sistem", value: formatIDR(rows.reduce((a, r) => a + Number(r.saldo_akhir || 0), 0)), cls: "text-[#0B1D3A]", testid: "bh-saldo-semua" },
          ].map((k) => (
            <div key={k.label} className="bg-white border border-slate-200 px-3 py-2" data-testid={k.testid}>
              <div className="overline text-[9px] text-slate-500">{k.label}</div>
              <div className={`font-display text-base font-bold tabular-nums ${k.cls}`}>{k.value}</div>
            </div>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-slate-400" data-testid="bank-history-empty">
          Belum ada rekening perusahaan. Tambahkan lewat Settings → Keuangan → Bank Perusahaan.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bank-history-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Rekening</th>
                <th className="text-right px-3 py-2">Total Mutasi Keluar</th>
                <th className="text-right px-3 py-2">Penerimaan Invoice</th>
                <th className="text-right px-3 py-2">Pemasukan Lainnya</th>
                <th className="text-right px-3 py-2">Total Mutasi Masuk</th>
                <th className="text-right px-3 py-2">Saldo Tercatat</th>
                <th className="text-right px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bank-history-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#0B1D3A]">{r.bank_name}</div>
                    <div className="text-[10px] text-slate-500 tabular-nums">{r.account_no}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700" data-testid={`bank-history-keluar-${r.id}`}>{formatIDR(r.total_keluar)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold" data-testid={`bank-history-invoice-${r.id}`}>{formatIDR(r.total_masuk_invoice || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700" data-testid={`bank-history-other-${r.id}`}>{formatIDR(r.total_masuk_other || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-bold" data-testid={`bank-history-masuk-${r.id}`}>{formatIDR(r.total_masuk)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.saldo_akhir < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`} data-testid={`bank-history-saldo-${r.id}`}>{formatIDR(r.saldo_akhir)}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" data-testid={`bank-history-ledger-${r.id}`} onClick={() => nav(`/kas-bank/ledger/${r.id}`)}
                      title={`Buka ledger ${r.bank_name}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
                      <BookOpen size={11} /> Ledger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
